import React from "react";
import { AtSign, Gift, Tag, UsersRound } from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTES } from "../../routes";
import AdminBlockToggle from "./AdminBlockToggle";
import { AdminUser, adminUserDisplayName } from "../../features/admin/models/AdminUser";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";
import AdminUserActivitySummary from "./AdminUserActivitySummary";
import AdminUserActivityLink from "./AdminUserActivityLink";

const formatDate = (date: Date): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);

const formatMoney = (minorUnits: number, currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);

/** "Custom €5.49/mo" / "Custom $79/yr" — the shortest read of the override. */
const customPriceLabel = (user: AdminUser): string | null => {
  if (!user.customPriceCurrency) {
    return null;
  }
  const parts: string[] = [];
  if (user.customMonthlyAmountMinorUnits != null) {
    parts.push(`${formatMoney(user.customMonthlyAmountMinorUnits, user.customPriceCurrency, getDisplayLocale())}/mo`);
  }
  if (user.customAnnualAmountMinorUnits != null) {
    parts.push(`${formatMoney(user.customAnnualAmountMinorUnits, user.customPriceCurrency, getDisplayLocale())}/yr`);
  }
  return parts.length > 0 ? `Custom ${parts.join(" · ")}` : null;
};

/** Badge styling per subscription state — green = active, red = lapsed, grey = neutral. */
const stateBadgeClass = (state: string | null): string => {
  switch (state) {
    case "Active":
      return "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300";
    case "Trialing":
      return "bg-secondary-90 dark:bg-secondary-20 text-secondary-20 dark:text-secondary-90";
    case "ReadOnly":
      return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300";
    default:
      return "bg-grey-90 dark:bg-primary-20 text-grey-30 dark:text-grey-70";
  }
};

/** "Owner · 3 members · +2 instructors" — the account's shape at a glance. */
const organizationSummary = (user: AdminUser): string => {
  // A row served by an API older than this app has no role/count fields yet.
  if (!user.organizationRole) {
    return "";
  }
  const parts = [
    user.organizationRole,
    `${user.memberCount} ${user.memberCount === 1 ? "member" : "members"}`,
  ];
  if (user.teamInstructorCount > 0) {
    parts.push(`+${user.teamInstructorCount} ${user.teamInstructorCount === 1 ? "instructor" : "instructors"}`);
  }
  return parts.join(" · ");
};

const subscriptionLabel = (user: AdminUser): string => {
  if (!user.hasSubscription || !user.subscriptionState) {
    return "No subscription";
  }
  if (user.subscriptionState === "Trialing" && user.trialDaysRemaining !== null) {
    return `Trial · ${user.trialDaysRemaining}d left`;
  }
  return user.subscriptionState;
};

interface AdminUserRowProps {
  user: AdminUser;
  /** Opens the free-access dialog for this instructor. */
  onManageComplimentary: (user: AdminUser) => void;
  /** Opens the login-email handover dialog for this instructor. */
  onChangeLoginEmail: (user: AdminUser) => void;
  /** Opens the custom-price dialog for this instructor. */
  onManageCustomPrice: (user: AdminUser) => void;
}

const AdminUserRow: React.FC<AdminUserRowProps> = ({
  user,
  onManageComplimentary,
  onChangeLoginEmail,
  onManageCustomPrice,
}) => (
  <div className="with-border flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4 lg:p-6">
    <div className="min-w-0">
      <p className="truncate font-semibold text-grey-10 dark:text-white">
        {adminUserDisplayName(user)}
      </p>
      <p className="truncate text-sm text-grey-40 dark:text-grey-70">{user.email}</p>
      <p className="mt-1 text-xs text-grey-50 dark:text-grey-60">
        Joined {formatDate(user.signupDate)}
        {organizationSummary(user) && ` · ${organizationSummary(user)}`}
      </p>
      <AdminUserActivitySummary user={user} />
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {user.complimentaryUntil && (
        <span className="rounded-full bg-primary-95 dark:bg-primary-30 px-3 py-1 text-xs font-medium text-primary-40 dark:text-primary-90">
          Free until {formatDate(user.complimentaryUntil)}
        </span>
      )}
      {customPriceLabel(user) && (
        <span className="rounded-full bg-tertiary-90 dark:bg-tertiary-20 px-3 py-1 text-xs font-medium text-tertiary-20 dark:text-tertiary-90">
          {customPriceLabel(user)}
        </span>
      )}
      {user.isDemo && (
        <span className="rounded-full bg-grey-90 dark:bg-primary-20 px-3 py-1 text-xs font-medium text-grey-30 dark:text-grey-70">
          Demo
        </span>
      )}
      {user.isFoundingMember && (
        <span className="rounded-full bg-tertiary-90 dark:bg-tertiary-20 px-3 py-1 text-xs font-medium text-tertiary-20 dark:text-tertiary-90">
          Founding
        </span>
      )}
      {user.instructorStatus !== "Active" && (
        <span className="rounded-full bg-grey-90 dark:bg-primary-20 px-3 py-1 text-xs font-medium text-grey-30 dark:text-grey-70">
          {user.instructorStatus}
        </span>
      )}
      <span
        className={`rounded-full px-3 py-1 text-xs font-medium ${stateBadgeClass(
          user.subscriptionState
        )}`}
      >
        {subscriptionLabel(user)}
      </span>
      <Link
        to={ROUTES.ADMIN_USER_MEMBERS.replace(":id", user.instructorId)}
        className="btn btn-secondary flex items-center gap-1.5"
      >
        <UsersRound size={16} />
        Members
      </Link>
      <AdminUserActivityLink instructorId={user.instructorId} />
      <button
        type="button"
        className="btn btn-secondary flex items-center gap-1.5"
        onClick={() => onManageComplimentary(user)}
      >
        <Gift size={16} />
        Free access
      </button>
      <button
        type="button"
        className="btn btn-secondary flex items-center gap-1.5"
        onClick={() => onManageCustomPrice(user)}
      >
        <Tag size={16} />
        Custom price
      </button>
      <button
        type="button"
        className="btn btn-secondary flex items-center gap-1.5"
        onClick={() => onChangeLoginEmail(user)}
        disabled={!user.userAccountId}
        title={user.userAccountId ? undefined : "No login account linked"}
      >
        <AtSign size={16} />
        Change email
      </button>
      {user.userAccountId && (
        <AdminBlockToggle kind="Account" principalId={user.userAccountId} name={adminUserDisplayName(user)} block={user.block} />
      )}
    </div>
  </div>
);

export default AdminUserRow;
